/**
 * Fondo vivo del menú principal.
 *
 * El menú era una pantalla quieta sobre una cancha congelada: parecía que el
 * juego no existía hasta que pulsabas algo. Aquí detrás siempre hay una partida
 * moviéndose, difuminada:
 *
 *  1. **En vivo**: si hay servidor y alguna sala jugando, se conecta como
 *     ESPECTADOR (solo lectura, ver `net/spectate.ts`) y lo que se ve por detrás
 *     es gente jugando de verdad, ahora mismo.
 *  2. **Exhibición**: si no hay ninguna sala abierta (o no hay backend), se
 *     simula en local una partida entre bots. Sin red, sin sala y sin
 *     interferir con nada: es el mismo `@tak/sim` corriendo en un rincón.
 *
 * La opción 2 no es un truco de relleno: es lo que hace que el menú se sienta
 * igual de vivo a las cuatro de la mañana del primer día, cuando no hay nadie
 * conectado — que es justo cuando más importa.
 */
import {
  GameState, InputState, POSSESSION_RANGE, createGame, createInputState,
  mapById, updateGameMulti,
} from "@tak/sim";
import { SpectatorSession } from "./net/spectate";

const FIXED_DT = 1 / 60;
/** Tope de pasos por frame: el fondo nunca debe robarle tiempo al menú. */
const MAX_STEPS = 3;
/** Cada cuánto se vuelve a mirar si ya hay salas en vivo que espectar (ms). */
const RESCAN_MS = 25000;
/** Jugadores de la partida de exhibición (2 contra 2). */
const AMBIENT_PLAYERS = 4;
/** Por debajo de esta rapidez (px/s) el balón cuenta como parado. */
const STUCK_SPEED = 60;
/** Segundos de balón parado antes de relanzarlo al centro (ver `unstick`). */
const STUCK_SECONDS = 2.5;

/** De dónde sale una sala en vivo para espectar. */
export interface LiveRoomSource {
  /** URL `wss://…?spectate=1&room=…` de una sala jugando, o null si no hay. */
  findLiveRoom(): Promise<string | null>;
}

export class MenuBackdrop {
  /** Partida local de exhibición (siempre existe: es el plan B inmediato). */
  private ambient: GameState;
  private readonly inputs: InputState[] = [];
  private acc = 0;
  private spectator: SpectatorSession | null = null;
  /** Reloj de la exhibición (segundos simulados). Da variedad sin aleatoriedad. */
  private ambientClock = 0;
  /** Segundos que el balón lleva prácticamente parado (ver `unstick`). */
  private stuckFor = 0;
  private running = false;
  private scanning = false;
  private lastScan = 0;

  constructor(private readonly source: LiveRoomSource | null = null) {
    this.ambient = this.newAmbient();
    for (let i = 0; i < AMBIENT_PLAYERS; i++) this.inputs.push(createInputState());
  }

  /**
   * El estado que toca dibujar de fondo.
   *
   * Se enseña la sala espectada SOLO si está jugando de verdad. Antes bastaba
   * con que el socket estuviera vivo, así que una sala en lobby o en pausa se
   * pintaba de fondo —congelada, con los discos clavados— mientras el cartel
   * decía "exhibición local". Lo que se ve y lo que se dice tienen que ser la
   * misma cosa: `game` e `isLive` responden ahora a la MISMA condición.
   */
  get game(): GameState {
    return this.isLive ? this.spectator!.game : this.ambient;
  }

  /** ¿Lo que se ve por detrás es una partida real de otra gente, en juego? */
  get isLive(): boolean {
    const spec = this.spectator;
    return !!spec && spec.live && spec.phase === "playing";
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.acc = 0;
    void this.scan();
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    this.dropSpectator();
  }

  /** Avanza el fondo. `dt` en segundos (ya acotado por el bucle de render). */
  update(dt: number): void {
    if (!this.running) return;
    const spec = this.spectator;
    if (spec && spec.live) spec.update(dt);
    // La exhibición sigue corriendo aunque haya espectador: si la sala espectada
    // está en lobby o en pausa, lo que se dibuja es esto, y tiene que estar vivo
    // desde el primer frame en que se necesite (no arrancando desde el saque).
    if (this.isLive) return;
    // Sin sala en vivo: corre la exhibición local y vuelve a buscar de vez en
    // cuando (alguien puede abrir una sala mientras se mira el menú).
    if (performance.now() - this.lastScan > RESCAN_MS) void this.scan();
    this.acc += Math.min(dt, 0.25);
    let steps = 0;
    while (this.acc >= FIXED_DT && steps < MAX_STEPS) {
      this.stepAmbient();
      this.acc -= FIXED_DT;
      steps++;
    }
    if (steps === MAX_STEPS) this.acc = 0;
  }

  /* ---------------- espectador ---------------- */

  private async scan(): Promise<void> {
    if (!this.source || this.scanning) return;
    this.scanning = true;
    this.lastScan = performance.now();
    try {
      const url = await this.source.findLiveRoom();
      // Mientras se buscaba, el jugador pudo salir del menú (o ya hay sesión).
      if (!url || !this.running || this.spectator) return;
      const spec = new SpectatorSession(url);
      spec.onEnd = () => { if (this.spectator === spec) this.dropSpectator(); };
      this.spectator = spec;
    } catch {
      // Sin lista de salas (servidor caído, CORS, offline): queda la exhibición.
    } finally {
      this.scanning = false;
      this.lastScan = performance.now();
    }
  }

  private dropSpectator(): void {
    this.spectator?.close();
    this.spectator = null;
  }

  /* ---------------- exhibición local ---------------- */

  private newAmbient(): GameState {
    // Sin límite de goles ni de tiempo: es un fondo, no tiene que terminar.
    return createGame(mapById("classic"), { goalLimit: 0, timeLimit: 0 }, { players: AMBIENT_PLAYERS });
  }

  private stepAmbient(): void {
    const g = this.ambient;
    this.ambientClock += FIXED_DT;
    for (let slot = 0; slot < g.players.length; slot++) {
      ambientInput(g, slot, this.inputs[slot], this.ambientClock);
    }
    updateGameMulti(g, this.inputs, FIXED_DT);
    // El marcador de la exhibición se reinicia solo: un fondo con 47-39 se lee
    // como un error, no como una partida.
    if (g.score[0] >= 5 || g.score[1] >= 5) { g.score[0] = 0; g.score[1] = 0; }
    this.unstick(g);
  }

  /**
   * Desatasca la exhibición.
   *
   * Una IA sencilla acaba, tarde o temprano, con el balón muerto en una esquina
   * y cuatro discos empujándose alrededor. De fondo eso no es "un partido": es
   * una pantalla parada que encima parece rota. Si la pelota lleva un rato
   * quieta, se relanza al centro con un empujón en una dirección cualquiera —
   * como un saque neutral de árbitro.
   */
  private unstick(g: GameState): void {
    const w = g.world;
    const speed = Math.hypot(w.vx[g.ball], w.vy[g.ball]);
    if (speed > STUCK_SPEED) { this.stuckFor = 0; return; }
    this.stuckFor += FIXED_DT;
    if (this.stuckFor < STUCK_SECONDS) return;
    this.stuckFor = 0;
    const angle = this.ambientClock * 2.399963; // ángulo áureo: nunca repite patrón
    w.px[g.ball] = g.courtW / 2;
    w.py[g.ball] = g.courtH / 2;
    w.vx[g.ball] = Math.cos(angle) * 520;
    w.vy[g.ball] = Math.sin(angle) * 320;
  }
}

/**
 * IA de la partida de exhibición.
 *
 * Cada equipo lleva un rematador y un defensor. El rematador no va al balón:
 * va DETRÁS del balón respecto a la portería rival, que es lo que hace que al
 * empujarlo o patearlo salga hacia la portería y no hacia cualquier lado — con
 * la IA "persigue la pelota" de siempre, los cuatro discos se apelotonaban en
 * el centro y el fondo parecía un forcejeo, no un partido.
 */
function ambientInput(g: GameState, slot: number, out: InputState, clock: number): void {
  const w = g.world;
  const body = g.players[slot];
  const ball = g.ball;
  const team = g.teams[slot];
  const bx = w.px[ball];
  const by = w.py[ball];
  const goalX = team === 0 ? g.courtW : 0; // portería que ataca este equipo
  const ownX = team === 0 ? 0 : g.courtW;
  // Un jugador de cada equipo se queda de guardameta (los slots alternan equipo,
  // así que el segundo de cada equipo es el de índice 2 y 3).
  const keeper = slot >= 2;
  /** ¿La pelota está en nuestra mitad? Decide si el portero sale a por ella. */
  const ballInOwnHalf = team === 0 ? bx < g.courtW * 0.42 : bx > g.courtW * 0.58;

  let tx: number;
  let ty: number;
  if (keeper && !ballInOwnHalf) {
    // Se planta en la línea de su área, siguiendo la altura del balón.
    tx = ownX + (team === 0 ? 150 : -150);
    ty = Math.max(g.courtH * 0.2, Math.min(g.courtH * 0.8, by));
  } else if (keeper) {
    // …pero SALE cuando el peligro está en su campo. Un portero clavado en la
    // línea mientras el balón le ronda el área es lo que hacía que el fondo se
    // viera como una demo y no como un partido.
    const dxg = goalX - bx;
    const dyg = g.courtH / 2 - by;
    const len = Math.hypot(dxg, dyg) || 1;
    tx = bx - (dxg / len) * 40;
    ty = by - (dyg / len) * 40;
  } else {
    const dxg = goalX - bx;
    const dyg = g.courtH / 2 - by;
    const len = Math.hypot(dxg, dyg) || 1;
    // El punto de ataque OSCILA un poco: sin esto los dos rematadores se colocan
    // exactamente igual partida tras partida y el fondo se repite como un GIF.
    const swing = Math.sin(clock * 0.7 + slot * 2.1) * 26;
    tx = bx - (dxg / len) * 34 - (dyg / len) * swing * 0.4;
    ty = by - (dyg / len) * 34 + (dxg / len) * swing * 0.4;
  }

  const dx = tx - w.px[body];
  const dy = ty - w.py[body];
  out.left = dx < -8;
  out.right = dx > 8;
  out.up = dy < -8;
  out.down = dy > 8;
  // Patea cuando tiene el balón y ya lo ha "cargado" un poco: como la patada va
  // en la dirección disco→balón y el disco se coloca por detrás, el disparo sale
  // hacia la portería contraria.
  const dist = Math.hypot(bx - w.px[body], by - w.py[body]);
  out.kick = dist <= POSSESSION_RANGE - 2 && g.possession[slot] > 0.25;
  out.kickRelease = false;
}
